import React from 'react';
import { X, CheckCircle, RefreshCw, AlertCircle, Upload, FileJson } from 'lucide-react';
import { productColorOptions } from '../utils/colors';

const Modals = ({
  // Certificate modals
  showAddModal,
  setShowAddModal,
  newDomain,
  setNewDomain,
  notifications,
  isChecking,
  addDomain,
  
  showEditModal,
  setShowEditModal,
  editingCertificate,
  setEditingCertificate,
  updateCertificate,
  
  showCertificateDetails,
  setShowCertificateDetails,
  selectedCertificate,
  refreshSingle,
  getStatusColor,
  getStatusIcon,
  formatDate,
  
  // Notification modals
  showAddNotificationModal,
  setShowAddNotificationModal,
  newNotification,
  setNewNotification,
  isSavingNotification,
  saveNotification,
  testNotification,
  
  showEditNotificationModal,
  setShowEditNotificationModal,
  editingNotification,
  setEditingNotification,
  updateNotification,
  
  // Product modals
  showAddProductModal,
  setShowAddProductModal,
  newProduct,
  setNewProduct,
  saveProduct,
  
  showEditProductModal,
  setShowEditProductModal,
  editingProduct,
  setEditingProduct,
  updateProduct,
  
  showDeleteConfirmModal,
  setShowDeleteConfirmModal,
  productToDelete,
  setProductToDelete,
  confirmDeleteProduct,
  
  showCloneModal,
  setShowCloneModal,
  cloneProduct,
  setCloneProduct,
  confirmCloneProduct,
  
  showImportModal,
  setShowImportModal,
  importMode,
  setImportMode,
  importFile,
  handleFileSelect,
  confirmImportProducts,
  fileInputRef,
  
  // Bulk notification modal
  showBulkNotificationModal,
  setShowBulkNotificationModal,
  bulkNotificationId,
  setBulkNotificationId,
  applyBulkNotification,
  selectedCertificatesCount,
  
  // Group modals
  groups,
  
  // Deployment modal
  selectedDeployment,
  setSelectedDeployment,
  
  // General modals
  showConfirmModal,
  setShowConfirmModal,
  confirmAction,
  
  alertModal,
  setAlertModal
}) => {
  return (
    <>
      {/* Add Domain Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 z-50">
          <div className="bg-gray-900 rounded-lg border border-gray-800 w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-800 flex items-center justify-between">
              <h2 className="text-lg font-bold">Add SSL Certificate</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {/* Domain */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Domain</label>
                <input
                  type="text"
                  value={newDomain.domain}
                  onChange={(e) => setNewDomain({...newDomain, domain: e.target.value})}
                  placeholder="example.com"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-sm"
                />
              </div>

              {/* Warning and Expiry Thresholds */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1.5">Warning Threshold</label>
                  <input
                    type="number"
                    min="1"
                    value={newDomain.warning_threshold}
                    onChange={(e) => {
                      const value = Math.max(1, Number(e.target.value));
                      setNewDomain({...newDomain, warning_threshold: value});
                    }}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-sm"
                  />
                  <p className="text-gray-400 text-xs mt-1">Days before expiry to show warning</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Expiry Threshold</label>
                  <input
                    type="number"
                    min="1"
                    value={newDomain.expiry_threshold}
                    onChange={(e) => {
                      const value = Math.max(1, Number(e.target.value));
                      setNewDomain({...newDomain, expiry_threshold: value});
                    }}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-sm"
                  />
                  <p className="text-gray-400 text-xs mt-1">Days before expiry to show critical alert</p>
                </div>
              </div>

              {/* Check Interval */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Check Interval (minutes)</label>
                <input
                  type="number"
                  min="1"
                  value={newDomain.check_interval}
                  onChange={(e) => {
                    const value = Math.max(1, Number(e.target.value));
                    setNewDomain({...newDomain, check_interval: value});
                  }}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-sm"
                />
                <p className="text-gray-400 text-xs mt-1">How often to check the certificate (in minutes)</p>
              </div>

              {/* Notification Selection */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Notification Channel (Optional)</label>
                <select
                  value={newDomain.notification_id || ''}
                  onChange={(e) => setNewDomain({...newDomain, notification_id: e.target.value ? parseInt(e.target.value) : null})}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-sm"
                >
                  <option value="">None</option>
                  {notifications.map((notif) => (
                    <option key={notif.id} value={notif.id}>
                      {notif.name} ({notif.type})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="p-4 border-t border-gray-800 flex justify-end gap-2">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-1.5 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={addDomain}
                disabled={isChecking || !newDomain.domain.trim()}
                className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-700 disabled:cursor-not-allowed text-sm"
              >
                {isChecking ? 'Adding...' : 'Add Certificate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Certificate Modal */}
      {showEditModal && editingCertificate && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 z-50">
          <div className="bg-gray-900 rounded-lg border border-gray-800 w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-800 flex items-center justify-between">
              <h2 className="text-lg font-bold">Edit SSL Certificate</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingCertificate(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1.5">Domain</label>
                <input
                  type="text"
                  value={editingCertificate.domain}
                  disabled
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 text-sm cursor-not-allowed"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1.5">Warning Threshold</label>
                  <input
                    type="number"
                    min="1"
                    value={editingCertificate.warning_threshold}
                    onChange={(e) => {
                      const value = Math.max(1, Number(e.target.value));
                      setEditingCertificate({...editingCertificate, warning_threshold: value});
                    }}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-sm"
                  />
                  <p className="text-gray-400 text-xs mt-1">Days before expiry to show warning</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Expiry Threshold</label>
                  <input
                    type="number"
                    min="1"
                    value={editingCertificate.expiry_threshold}
                    onChange={(e) => {
                      const value = Math.max(1, Number(e.target.value));
                      setEditingCertificate({...editingCertificate, expiry_threshold: value});
                    }}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-sm"
                  />
                  <p className="text-gray-400 text-xs mt-1">Days before expiry to show critical alert</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5">Check Interval (minutes)</label>
                <input
                  type="number"
                  min="1"
                  value={editingCertificate.check_interval}
                  onChange={(e) => {
                    const value = Math.max(1, Number(e.target.value));
                    setEditingCertificate({...editingCertificate, check_interval: value});
                  }}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-sm"
                />
                <p className="text-gray-400 text-xs mt-1">How often to check the certificate (in minutes)</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5">Notification Channel (Optional)</label>
                <select
                  value={editingCertificate.notification_id || ''}
                  onChange={(e) => setEditingCertificate({...editingCertificate, notification_id: e.target.value ? parseInt(e.target.value) : null})}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-sm"
                >
                  <option value="">None</option>
                  {notifications.map((notif) => (
                    <option key={notif.id} value={notif.id}>
                      {notif.name} ({notif.type})
                    </option>
                  ))}
                </select>
                <p className="text-gray-400 text-xs mt-1">Choose a template for SSL certificate alert messages</p>
              </div>
            </div>

            <div className="p-4 border-t border-gray-800 flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingCertificate(null);
                }}
                className="px-4 py-1.5 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={updateCertificate}
                className="px-4 py-1.5 bg-white text-gray-900 rounded-lg hover:bg-gray-100 transition-colors font-medium text-sm"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Certificate Details Modal */}
      {showCertificateDetails && selectedCertificate && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 z-50">
          <div className="bg-gray-900 rounded-lg border border-gray-800 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-800 flex items-center justify-between sticky top-0 bg-gray-900">
              <div>
                <h2 className="text-lg font-bold">{selectedCertificate.domain}</h2>
                <div className="flex items-center gap-2 mt-1">
                  {getStatusIcon(selectedCertificate.status)}
                  <span className={`text-sm font-medium ${getStatusColor(selectedCertificate.status)}`}>
                    {selectedCertificate.status.charAt(0).toUpperCase() + selectedCertificate.status.slice(1)}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setShowCertificateDetails(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-sm mb-1">Issuer</p>
                  <p className="text-white font-medium">{selectedCertificate.issuer || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Valid From</p>
                  <p className="text-white font-medium">{formatDate(selectedCertificate.valid_from)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Valid Until</p>
                  <p className="text-white font-medium">{formatDate(selectedCertificate.valid_until)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Days Remaining</p>
                  <p className={`font-semibold ${
                    selectedCertificate.days_left < 0 ? 'text-red-400' :
                    selectedCertificate.days_left <= 7 ? 'text-red-400' :
                    selectedCertificate.days_left <= 30 ? 'text-yellow-400' :
                    'text-green-400'
                  }`}>
                    {selectedCertificate.days_left} days
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Last Checked</p>
                  <p className="text-white font-medium">{formatDate(selectedCertificate.last_checked)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Check Interval</p>
                  <p className="text-white font-medium">{selectedCertificate.check_interval} minutes</p>
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-3">
                <p className="text-gray-400 text-sm mb-2">Certificate Thresholds</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-gray-400 text-xs">Warning</p>
                    <p className="text-yellow-400 font-medium">{selectedCertificate.warning_threshold} days</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-xs">Critical</p>
                    <p className="text-red-400 font-medium">{selectedCertificate.expiry_threshold} days</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border-t border-gray-800 flex justify-end gap-2 sticky bottom-0 bg-gray-900">
              <button
                onClick={() => setShowCertificateDetails(false)}
                className="px-4 py-1.5 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
              >
                Close
              </button>
              <button
                onClick={() => {
                  refreshSingle(selectedCertificate.id);
                  setShowCertificateDetails(false);
                }}
                className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm"
              >
                <RefreshCw className="w-4 h-4" />
                Check Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Notification Modal */}
      {showAddNotificationModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-xl border border-gray-800 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between sticky top-0 bg-gray-900">
              <h2 className="text-2xl font-bold">Setup New Notification</h2>
              <button
                onClick={() => setShowAddNotificationModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Friendly Name</label>
                <input
                  type="text"
                  value={newNotification.name}
                  onChange={(e) => setNewNotification({...newNotification, name: e.target.value})}
                  placeholder="My Discord Alert"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Notification Type</label>
                <select
                  value={newNotification.type}
                  onChange={(e) => setNewNotification({...newNotification, type: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                >
                  <option value="discord">Discord</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Webhook URL</label>
                <input
                  type="url"
                  value={newNotification.webhook_url}
                  onChange={(e) => setNewNotification({...newNotification, webhook_url: e.target.value})}
                  placeholder="https://discord.com/api/webhooks/..."
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Bot Display Name</label>
                <input
                  type="text"
                  value={newNotification.bot_name}
                  onChange={(e) => setNewNotification({...newNotification, bot_name: e.target.value})}
                  placeholder="SSL Monitor"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Prefix Custom Message (Optional)</label>
                <textarea
                  value={newNotification.prefix_message}
                  onChange={(e) => setNewNotification({...newNotification, prefix_message: e.target.value})}
                  placeholder="@everyone SSL Alert"
                  rows={2}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Notification Resend Interval</label>
                <select
                  value={newNotification.resend_times}
                  onChange={(e) => setNewNotification({...newNotification, resend_times: parseInt(e.target.value)})}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                >
                  <option value={1}>Every check (1x)</option>
                  <option value={2}>Every 2 checks (2x)</option>
                  <option value={3}>Every 3 checks (3x)</option>
                  <option value={5}>Every 5 checks (5x)</option>
                  <option value={10}>Every 10 checks (10x)</option>
                </select>
                <p className="text-gray-400 text-xs mt-1">How often to send notifications based on SSL check intervals</p>
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newNotification.default_enabled}
                    onChange={(e) => setNewNotification({...newNotification, default_enabled: e.target.checked})}
                    className="w-4 h-4 rounded"
                  />
                  <span>Default enabled for all certificates</span>
                </label>
              </div>
            </div>

            <div className="p-6 border-t border-gray-800 flex justify-end gap-3 sticky bottom-0 bg-gray-900">
              <button
                onClick={() => setShowAddNotificationModal(false)}
                className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={testNotification}
                className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium"
              >
                Test
              </button>
              <button 
                onClick={() => {
                  saveNotification();
                  setShowAddNotificationModal(false);
                }}
                disabled={isSavingNotification}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:bg-gray-700 disabled:cursor-not-allowed"
              >
                {isSavingNotification ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Notification Modal */}
      {showEditNotificationModal && editingNotification && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-xl border border-gray-800 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between sticky top-0 bg-gray-900">
              <h2 className="text-2xl font-bold">Edit Notification</h2>
              <button
                onClick={() => {
                  setShowEditNotificationModal(false);
                  setEditingNotification(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Friendly Name</label>
                <input
                  type="text"
                  value={editingNotification.name}
                  onChange={(e) => setEditingNotification({...editingNotification, name: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Webhook URL</label>
                <input
                  type="url"
                  value={editingNotification.webhook_url}
                  onChange={(e) => setEditingNotification({...editingNotification, webhook_url: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Bot Display Name</label>
                <input
                  type="text"
                  value={editingNotification.bot_name}
                  onChange={(e) => setEditingNotification({...editingNotification, bot_name: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Prefix Custom Message</label>
                <textarea
                  value={editingNotification.prefix_message}
                  onChange={(e) => setEditingNotification({...editingNotification, prefix_message: e.target.value})}
                  rows={2}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Notification Resend Interval</label>
                <select
                  value={editingNotification.resend_times || 2}
                  onChange={(e) => setEditingNotification({...editingNotification, resend_times: parseInt(e.target.value)})}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                >
                  <option value={1}>Every check (1x)</option>
                  <option value={2}>Every 2 checks (2x)</option>
                  <option value={3}>Every 3 checks (3x)</option>
                  <option value={5}>Every 5 checks (5x)</option>
                  <option value={10}>Every 10 checks (10x)</option>
                </select>
                <p className="text-gray-400 text-xs mt-1">How often to send notifications based on SSL check intervals</p>
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingNotification.default_enabled === 1}
                    onChange={(e) => setEditingNotification({...editingNotification, default_enabled: e.target.checked ? 1 : 0})}
                    className="w-4 h-4 rounded"
                  />
                  <span>Default enabled for all certificates</span>
                </label>
              </div>
            </div>

            <div className="p-6 border-t border-gray-800 flex justify-end gap-3 sticky bottom-0 bg-gray-900">
              <button 
                onClick={() => {
                  setShowEditNotificationModal(false);
                  setEditingNotification(null);
                }}
                className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
              >
                Cancel
              </button>
              <button 
                onClick={updateNotification}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deployment Details Modal */}
      {selectedDeployment && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-xl border border-gray-800 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-5 border-b border-gray-800 flex items-center justify-between bg-gradient-to-r from-gray-900 to-gray-800/50">
              <div>
                <h2 className="text-2xl font-bold">{selectedDeployment.tag}</h2>
              </div>
              <button 
                onClick={() => setSelectedDeployment(null)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {selectedDeployment.details && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-lg p-3.5 hover:border-blue-500/40 transition-colors">
                    <p className="text-xs font-medium text-blue-400 mb-1.5 uppercase tracking-wide">Build Number</p>
                    <p className="text-white font-mono text-sm">{selectedDeployment.details.build_number}</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20 rounded-lg p-3.5 hover:border-purple-500/40 transition-colors">
                    <p className="text-xs font-medium text-purple-400 mb-1.5 uppercase tracking-wide">Commit ID</p>
                    <p className="text-white font-mono text-sm truncate" title={selectedDeployment.details.commit_id}>{selectedDeployment.details.commit_id}</p>
                  </div>
                  <div className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/20 rounded-lg p-3.5 hover:border-orange-500/40 transition-colors">
                    <p className="text-xs font-medium text-orange-400 mb-1.5 uppercase tracking-wide">Branch Name</p>
                    <p className="text-white font-mono text-sm">{selectedDeployment.branch || selectedDeployment.details.branch_name || 'N/A'}</p>
                  </div>
                  {selectedDeployment.approved_by && (
                    <div className="bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border border-cyan-500/20 rounded-lg p-3.5 hover:border-cyan-500/40 transition-colors">
                      <p className="text-xs font-medium text-cyan-400 mb-1.5 uppercase tracking-wide">Approved By</p>
                      <p className="text-white text-sm">{selectedDeployment.approved_by}</p>
                    </div>
                  )}
                  <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20 rounded-lg p-3.5 hover:border-green-500/40 transition-colors md:col-span-2">
                    <p className="text-xs font-medium text-green-400 mb-1.5 uppercase tracking-wide">Commit Message</p>
                    <p className="text-white text-sm">{selectedDeployment.details.commit_message}</p>
                  </div>
                  <div className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border border-yellow-500/20 rounded-lg p-3.5 hover:border-yellow-500/40 transition-colors">
                    <p className="text-xs font-medium text-yellow-400 mb-1.5 uppercase tracking-wide">Author</p>
                    <p className="text-white text-sm">{selectedDeployment.details.author_name}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{selectedDeployment.details.author_email}</p>
                  </div>
                  <div className="bg-gradient-to-br from-pink-500/10 to-pink-600/5 border border-pink-500/20 rounded-lg p-3.5 hover:border-pink-500/40 transition-colors">
                    <p className="text-xs font-medium text-pink-400 mb-1.5 uppercase tracking-wide">Deployed At</p>
                    <p className="text-white text-sm">{selectedDeployment.details?.date_time ? (() => {
                      // Parse the date string as-is without timezone conversion
                      const dateStr = selectedDeployment.details.date_time;
                      const date = new Date(dateStr.replace(' ', 'T')); // Convert to ISO format
                      return date.toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                        hour12: true
                      });
                    })() : 'No date available'}</p>
                  </div>
                  <div className="bg-gradient-to-br from-indigo-500/10 to-indigo-600/5 border border-indigo-500/20 rounded-lg p-3.5 hover:border-indigo-500/40 transition-colors">
                    <p className="text-xs font-medium text-indigo-400 mb-1.5 uppercase tracking-wide">Domain</p>
                    <p className="text-white font-mono text-sm break-all">{selectedDeployment.details.domain_name}</p>
                  </div>
                  <div className="bg-gradient-to-br from-teal-500/10 to-teal-600/5 border border-teal-500/20 rounded-lg p-3.5 hover:border-teal-500/40 transition-colors">
                    <p className="text-xs font-medium text-teal-400 mb-1.5 uppercase tracking-wide">Image Name</p>
                    <p className="text-white font-mono text-sm break-all">{selectedDeployment.details.image_name}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-gray-800 bg-gray-900/50 flex justify-end">
              <button 
                onClick={() => setSelectedDeployment(null)}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-medium shadow-lg shadow-blue-600/20 hover:shadow-blue-600/40"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddProductModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-xl border border-gray-800 w-full max-w-md">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between">
              <h2 className="text-2xl font-bold">Add New Product</h2>
              <button
                onClick={() => setShowAddProductModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Product ID</label>
                <input
                  type="text"
                  value={newProduct.id}
                  onChange={(e) => setNewProduct({...newProduct, id: e.target.value})}
                  placeholder="MyApp-1"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                />
                <p className="text-gray-400 text-xs mt-1">Format: name-number (e.g., QuizApp-1, Unidash-2)</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Product Name</label>
                <input
                  type="text"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                  placeholder="My Product"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description (Optional)</label>
                <textarea
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                  placeholder="Product description"
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Color</label>
                <select
                  value={newProduct.color}
                  onChange={(e) => setNewProduct({...newProduct, color: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                >
                  {productColorOptions.map((color) => (
                    <option key={color.value} value={color.value}>
                      {color.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Version URL (Optional)</label>
                <input
                  type="url"
                  value={newProduct.version_url}
                  onChange={(e) => setNewProduct({...newProduct, version_url: e.target.value})}
                  placeholder="https://example.com/api/_version"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                />
                <p className="text-gray-400 text-xs mt-1">URL to check live deployment version</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Group <span className="text-red-400">*</span></label>
                <select
                  value={newProduct.group_id || ''}
                  onChange={(e) => setNewProduct({...newProduct, group_id: e.target.value ? parseInt(e.target.value) : null})}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                  required
                >
                  <option value="">Select a group</option>
                  {groups && groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                <p className="text-gray-400 text-xs mt-1">Assign product to a group for easier filtering</p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
              <button
                onClick={() => setShowAddProductModal(false)}
                className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={saveProduct}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Save Product
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Product Modal */}
      {showEditProductModal && editingProduct && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-xl border border-gray-800 w-full max-w-md">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between">
              <h2 className="text-2xl font-bold">Edit Product</h2>
              <button
                onClick={() => {
                  setShowEditProductModal(false);
                  setEditingProduct(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Product ID</label>
                <div className="px-4 py-2 bg-gray-800/50 border border-gray-700/50 rounded-lg text-gray-400 font-mono text-sm">
                  {editingProduct.id}
                </div>
                <p className="text-gray-500 text-xs mt-1">Product ID cannot be changed</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Product Name</label>
                <input
                  type="text"
                  value={editingProduct.name}
                  onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={editingProduct.description || ''}
                  onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Color</label>
                <select
                  value={editingProduct.color}
                  onChange={(e) => setEditingProduct({...editingProduct, color: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                >
                  {productColorOptions.map((color) => (
                    <option key={color.value} value={color.value}>
                      {color.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Version URL (Optional)</label>
                <input
                  type="url"
                  value={editingProduct.version_url || ''}
                  onChange={(e) => setEditingProduct({...editingProduct, version_url: e.target.value})}
                  placeholder="https://example.com/api/_version"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                />
                <p className="text-gray-400 text-xs mt-1">URL to check live deployment version</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Group <span className="text-red-400">*</span></label>
                <select
                  value={editingProduct.group_id || ''}
                  onChange={(e) => setEditingProduct({...editingProduct, group_id: e.target.value ? parseInt(e.target.value) : null})}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                  required
                >
                  <option value="">Select a group</option>
                  {groups && groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                <p className="text-gray-400 text-xs mt-1">Assign product to a group for easier filtering</p>
              </div>
            </div>

            <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowEditProductModal(false);
                  setEditingProduct(null);
                }}
                className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={updateProduct}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && productToDelete && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-xl border border-gray-800 w-full max-w-md">
            <div className="p-6 border-b border-gray-800">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-6 h-6 text-red-400" />
                </div>
                <h2 className="text-xl font-bold">Delete Product</h2>
              </div>
              <p className="text-gray-400 mt-4">
                Are you sure you want to delete <span className="text-white font-semibold">"{productToDelete.name}"</span>? This action cannot be undone.
              </p>
            </div>

            <div className="p-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirmModal(false);
                  setProductToDelete(null);
                }}
                className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteProduct}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirmModal && confirmAction && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-xl border border-gray-800 w-full max-w-md">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">{confirmAction.title}</h2>
              <p className="text-gray-400 mb-6">{confirmAction.message}</p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAction.onConfirm}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      {alertModal.show && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-xl border border-gray-800 w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  alertModal.type === 'success' ? 'bg-green-500/20' : 'bg-red-500/20'
                }`}>
                  {alertModal.type === 'success' ? (
                    <CheckCircle className="w-6 h-6 text-green-400" />
                  ) : (
                    <X className="w-6 h-6 text-red-400" />
                  )}
                </div>
                <h2 className="text-xl font-bold">{alertModal.type === 'success' ? 'Success' : 'Error'}</h2>
              </div>
              <p className="text-gray-400 mb-6">{alertModal.message}</p>
              <div className="flex justify-end">
                <button
                  onClick={() => setAlertModal({ show: false, message: '', type: 'success' })}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Products Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-lg border border-gray-800 w-full max-w-lg">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Upload className="w-6 h-6 text-purple-400" />
                <h2 className="text-xl font-bold">Import Products</h2>
              </div>
              <button
                onClick={() => setShowImportModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Import Mode Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Import Mode</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-750 transition-colors">
                    <input
                      type="radio"
                      name="importMode"
                      value="merge"
                      checked={importMode === 'merge'}
                      onChange={(e) => setImportMode(e.target.value)}
                      className="w-4 h-4 text-purple-600"
                    />
                    <div>
                      <div className="font-medium">Merge</div>
                      <div className="text-xs text-gray-400">Keep existing products and add new ones</div>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-750 transition-colors">
                    <input
                      type="radio"
                      name="importMode"
                      value="replace"
                      checked={importMode === 'replace'}
                      onChange={(e) => setImportMode(e.target.value)}
                      className="w-4 h-4 text-purple-600"
                    />
                    <div>
                      <div className="font-medium">Replace</div>
                      <div className="text-xs text-gray-400">Update existing products with imported data</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* File Selection */}
              <div>
                <label className="block text-sm font-medium mb-2">Select JSON File</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full px-4 py-3 bg-gray-800 border-2 border-dashed border-gray-700 rounded-lg hover:border-purple-500 hover:bg-gray-750 transition-colors flex items-center justify-center gap-2"
                >
                  <FileJson className="w-5 h-5 text-purple-400" />
                  <span>{importFile ? importFile.name : 'Choose file...'}</span>
                </button>
                <p className="text-xs text-gray-400 mt-2">Select a JSON file exported from this application</p>
              </div>

              {/* Import Info */}
              {importFile && (
                <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-400">
                      <div className="font-medium mb-1">File selected: {importFile.name}</div>
                      <div className="text-xs">
                        {importMode === 'merge' 
                          ? 'New products and builds will be added. Existing products will be kept as is.'
                          : 'Existing products with the same ID will be updated with imported data.'}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
              <button
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmImportProducts}
                disabled={!importFile}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-700 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Import
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clone Product Modal */}
      {showCloneModal && cloneProduct && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-lg border border-gray-800 w-full max-w-lg">
            <div className="p-6 border-b border-gray-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <RefreshCw className="w-6 h-6 text-blue-400" />
                <h2 className="text-xl font-bold">Clone Product</h2>
              </div>
              <button
                onClick={() => setShowCloneModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Product ID</label>
                <input
                  type="text"
                  value={cloneProduct.id}
                  onChange={(e) => setCloneProduct({...cloneProduct, id: e.target.value})}
                  placeholder="e.g., MyApp-1"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                />
                <p className="text-xs text-gray-400 mt-1">Format: name-number (e.g., MyApp-1, QuizApp-2)</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Product Name</label>
                <input
                  type="text"
                  value={cloneProduct.name}
                  onChange={(e) => setCloneProduct({...cloneProduct, name: e.target.value})}
                  placeholder="Product name"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description (Optional)</label>
                <textarea
                  value={cloneProduct.description}
                  onChange={(e) => setCloneProduct({...cloneProduct, description: e.target.value})}
                  placeholder="Product description"
                  rows={2}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Group</label>
                <select
                  value={cloneProduct.group_id || ''}
                  onChange={(e) => setCloneProduct({...cloneProduct, group_id: e.target.value ? parseInt(e.target.value) : null})}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                >
                  <option value="">Select a group...</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Version URL (Optional)</label>
                <input
                  type="text"
                  value={cloneProduct.version_url}
                  onChange={(e) => setCloneProduct({...cloneProduct, version_url: e.target.value})}
                  placeholder="https://example.com/version"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Color</label>
                <select
                  value={cloneProduct.color}
                  onChange={(e) => setCloneProduct({...cloneProduct, color: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white"
                >
                  <option value="blue">Blue</option>
                  <option value="green">Green</option>
                  <option value="red">Red</option>
                  <option value="yellow">Yellow</option>
                  <option value="purple">Purple</option>
                  <option value="pink">Pink</option>
                  <option value="indigo">Indigo</option>
                  <option value="orange">Orange</option>
                </select>
              </div>

              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-blue-400">
                    This will create a new product with the settings above. Deployment history will not be copied.
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
              <button
                onClick={() => setShowCloneModal(false)}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmCloneProduct}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Clone Product
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Notification Modal */}
      {showBulkNotificationModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-lg border border-gray-800 w-full max-w-md">
            <div className="p-6 border-b border-gray-800">
              <h2 className="text-xl font-bold">Set Notification for Selected Certificates</h2>
              <p className="text-sm text-gray-400 mt-1">
                Apply notification to {selectedCertificatesCount} selected certificate{selectedCertificatesCount > 1 ? 's' : ''}
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Select Notification Channel</label>
                <select
                  value={bulkNotificationId}
                  onChange={(e) => setBulkNotificationId(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-white"
                >
                  <option value="">Choose notification...</option>
                  <option value="none">❌ Remove Notification</option>
                  {notifications.map((notif) => (
                    <option key={notif.id} value={notif.id}>
                      {notif.name} ({notif.type})
                    </option>
                  ))}
                </select>
              </div>

              <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-purple-400">
                    This will {bulkNotificationId === 'none' ? 'remove notifications from' : 'assign the selected notification to'} all {selectedCertificatesCount} selected certificate{selectedCertificatesCount > 1 ? 's' : ''}.
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
              <button
                onClick={() => setShowBulkNotificationModal(false)}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={applyBulkNotification}
                disabled={!bulkNotificationId}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-700 disabled:cursor-not-allowed"
              >
                Apply to Selected
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Modals;
